import React, { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../services/firebase';
import { doc, setDoc, getDoc, getDocs, collection, deleteDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Calendar, Clock, Trash2, Plus, Loader2 } from 'lucide-react';

const AdminMatches = () => {
  const [user, loading] = useAuthState(auth);
  const [isAdmin, setIsAdmin] = useState(false);
  const [matches, setMatches] = useState([]);
  const [search, setSearch] = useState('');
  const [addedMatches, setAddedMatches] = useState([]);
  const [apiLoading, setApiLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('api'); // 'api', 'live', 'upcoming'
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserRole = async () => {
      if (user) {
        const ref = doc(db, 'users', user.uid);
        const snap = await getDoc(ref);
        if (snap.exists() && snap.data().role === 'admin') {
          setIsAdmin(true);
        } else {
          navigate('/');
        }
      }
    };
    fetchUserRole();
  }, [user, navigate]);

  useEffect(() => {
    const fetchMatches = async () => {
      setApiLoading(true);
      try {
        const res = await axios.get(
          'https://api.cricapi.com/v1/cricScore?apikey=0d8f84db-d050-4dab-b799-7e9fab1254bc'
        );
        if (res.data && res.data.data) {
          setMatches(res.data.data);
        }
      } catch (err) {
        console.error('Error fetching matches:', err);
      } finally {
        setApiLoading(false);
      }
    };

    const fetchAddedMatches = async () => {
      const snap = await getDocs(collection(db, 'matches'));
      const added = [];
      snap.forEach(doc => {
        added.push(doc.data());
      });
      setAddedMatches(added);
    };

    fetchMatches();
    fetchAddedMatches();
  }, []);

  const handleAddMatch = async (match) => {
    const cricbuzzId = prompt('Enter Cricbuzz ID for this match:');
    if (cricbuzzId === null || cricbuzzId.trim() === '') {
      alert('Cricbuzz ID is required!');
      return;
    }

    const matchData = {
      id: match.id,
      cricbuzzId: cricbuzzId,
      name: `${match.t1} vs ${match.t2}`,
      dateTimeGMT: match.dateTimeGMT,
      date: match.dateTimeGMT,
      team1: match.t1,
      team2: match.t2,
      team1img: match.t1img || '',
      team2img: match.t2img || '',
      series: match.series,
      result: '',
      scorecard: {},
      matchType: match.matchType
    };

    try {
      await setDoc(doc(db, 'matches', match.id), matchData);
      alert('Match added to Firestore!');
      setAddedMatches((prev) => [...prev, matchData]);
    } catch (err) {
      console.error('Error adding match:', err);
      alert('Error saving match.');
    }
  };

  const handleDeleteMatch = async (matchId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this match?');
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, 'matches', matchId));
      alert('Match deleted!');
      setAddedMatches((prev) => prev.filter(m => m.id !== matchId));
    } catch (err) {
      console.error('Error deleting match:', err);
      alert('Error deleting match.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
        <span className="ml-2 text-lg text-gray-200">Loading...</span>
      </div>
    );
  }
  
  if (!isAdmin) return null;

  const filteredMatches = matches.filter(match =>
    `${match.t1} vs ${match.t2}`.toLowerCase().includes(search.toLowerCase()) ||
    match.series.toLowerCase().includes(search.toLowerCase())
  );

  const now = new Date();

  const liveMatches = addedMatches.filter(match => new Date(match.dateTimeGMT) <= now);
  const upcomingMatches = addedMatches.filter(match => new Date(match.dateTimeGMT) > now);

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-900 min-h-screen">
      <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
        <h1 className="text-2xl font-bold text-gray-100 mb-6">Admin Match Management</h1>
        
        {/* Search Bar */}
        <div className="mb-6 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search matches by team or series..."
            className="pl-10 w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-gray-100 placeholder-gray-400"
          />
        </div>
        
        {/* Tab Navigation */}
        <div className="border-b border-gray-700 mb-6">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('api')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'api'
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'
              }`}
            >
              API Matches
            </button>
            <button
              onClick={() => setActiveTab('live')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'live'
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'
              }`}
            >
              Live Matches ({liveMatches.length})
            </button>
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'upcoming'
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'
              }`}
            >
              Upcoming Matches ({upcomingMatches.length})
            </button>
          </nav>
        </div>
        
        {/* Content based on active tab */}
        {activeTab === 'api' && (
          <>
            <h2 className="text-xl font-semibold mb-4 text-gray-200">Available Matches</h2>
            {apiLoading ? (
              <div className="flex justify-center items-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                <span className="ml-2 text-gray-300">Loading matches...</span>
              </div>
            ) : filteredMatches.length === 0 ? (
              <div className="text-center py-12 bg-gray-700 rounded-lg">
                <p className="text-gray-300">No matches found matching your search criteria.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredMatches.map((match) => (
                  <div key={match.id} className="bg-gray-750 border border-gray-700 rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                    <div className="p-5">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-lg text-gray-100">{match.t1} vs {match.t2}</h3>
                          <p className="text-sm text-gray-400 mt-1">{match.series}</p>
                        </div>
                        <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-xs font-medium">
                          {match.matchType || 'Unknown Type'}
                        </span>
                      </div>
                      
                      <div className="mt-4 flex items-center text-sm text-gray-400">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>{new Date(match.dateTimeGMT).toLocaleDateString()}</span>
                        <Clock className="h-4 w-4 ml-3 mr-1" />
                        <span>{new Date(match.dateTimeGMT).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                      </div>
                      
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-300">
                          Status: <span className="text-blue-400">{match.status}</span>
                        </p>
                      </div>
                      
                      <button
                        onClick={() => handleAddMatch(match)}
                        className="mt-4 w-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add to Database
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        
        {activeTab === 'live' && (
          <>
            <h2 className="text-xl font-semibold mb-4 text-gray-200 flex items-center">
              <span className="h-2 w-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
              Live Matches
            </h2>
            {liveMatches.length === 0 ? (
              <div className="text-center py-12 bg-gray-700 rounded-lg">
                <p className="text-gray-300">No live matches currently in the database.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {liveMatches.map((match) => (
                  <div key={match.id} className="bg-gray-750 border border-green-900 rounded-lg shadow-md overflow-hidden">
                    <div className="bg-green-900/30 px-4 py-2 border-b border-green-900 flex justify-between items-center">
                      <div className="flex items-center">
                        <span className="h-2 w-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                        <span className="text-green-400 font-medium">LIVE NOW</span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {match.matchType || 'Match'}
                      </span>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-lg text-gray-100">{match.name}</h3>
                      {match.series && <p className="text-sm text-gray-400 mt-1">{match.series}</p>}
                      
                      <div className="mt-3 flex items-center text-sm text-gray-400">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>{new Date(match.dateTimeGMT).toLocaleDateString()}</span>
                        <Clock className="h-4 w-4 ml-3 mr-1" />
                        <span>{new Date(match.dateTimeGMT).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                      </div>
                      
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={() => handleDeleteMatch(match.id)}
                          className="flex items-center text-red-400 hover:text-red-300 transition-colors"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        
        {activeTab === 'upcoming' && (
          <>
            <h2 className="text-xl font-semibold mb-4 text-gray-200">Upcoming Matches</h2>
            {upcomingMatches.length === 0 ? (
              <div className="text-center py-12 bg-gray-700 rounded-lg">
                <p className="text-gray-300">No upcoming matches in the database.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {upcomingMatches.map((match) => (
                  <div key={match.id} className="bg-gray-750 border border-yellow-900 rounded-lg shadow-md overflow-hidden">
                    <div className="bg-yellow-900/30 px-4 py-2 border-b border-yellow-900 flex justify-between items-center">
                      <span className="text-yellow-400 font-medium">Upcoming</span>
                      <span className="text-xs text-gray-400">
                        {match.matchType || 'Match'}
                      </span>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-lg text-gray-100">{match.name}</h3>
                      {match.series && <p className="text-sm text-gray-400 mt-1">{match.series}</p>}
                      
                      <div className="mt-3 flex items-center text-sm text-gray-400">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>{new Date(match.dateTimeGMT).toLocaleDateString()}</span>
                        <Clock className="h-4 w-4 ml-3 mr-1" />
                        <span>{new Date(match.dateTimeGMT).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                      </div>
                      
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={() => handleDeleteMatch(match.id)}
                          className="flex items-center text-red-400 hover:text-red-300 transition-colors"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminMatches;
